import {
  User,
  Mail,
  MapPin,
  Building,
  Briefcase,
  DollarSign,
  Twitter,
  Linkedin,
  CheckCircle,
  Loader,
  Camera,
} from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import type { ProfileScreenViewProps } from './ProfileScreenViewProps'

export const ProfileScreenDesktop = ({
  profile,
  formData,
  uploadingPicture,
  updating,
  validationErrors,
  fileInputRef,
  getProfilePictureUrl,
  getUserInitials,
  onProfilePictureChange,
  onInputChange,
  onSubmit,
  desktopInputClass,
  hasMinAmount,
  hasMaxAmount,
  countryDisplay,
  formatCurrencyDesktop,
}: ProfileScreenViewProps) => {
  if (!profile) return null

  return (
    <div className="space-y-4">
      <div className="border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <form onSubmit={onSubmit} className="space-y-0">
          <div className="grid grid-cols-[240px_1fr] gap-8 border-b border-[rgba(45,41,35,0.12)] px-8 py-8 dark:border-[#171717]">
            <div className="space-y-4">
              <h2 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                Basic Information
              </h2>

              <div className="relative group w-max">
                <div className="h-20 w-20 rounded-full overflow-hidden bg-[rgba(255,255,255,0.72)] border border-[rgba(45,41,35,0.12)] dark:bg-[#1c1c1c] dark:border-[rgba(253,253,253,0.12)]">
                  {getProfilePictureUrl() ? (
                    <img
                      src={getProfilePictureUrl() || ''}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-[rgba(45,41,35,0.08)] flex items-center justify-center dark:bg-[rgba(253,253,253,0.08)]">
                      <span className="font-plus-jakarta text-[24px] font-semibold text-[#2d2923] dark:text-[#fdfdfd]">
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPicture}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 flex items-center justify-center disabled:cursor-not-allowed"
                >
                  {uploadingPicture ? (
                    <Loader className="h-5 w-5 text-white animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5 text-white" />
                  )}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={onProfilePictureChange}
                  className="hidden"
                />
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[#2d2923] underline underline-offset-2 disabled:opacity-50 dark:text-[#fdfdfd]"
              >
                {uploadingPicture ? 'Uploading...' : 'Change photo'}
              </button>

              {validationErrors.profile_picture && (
                <p className="text-[12px] leading-[16px] text-red-500">
                  {validationErrors.profile_picture}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  First name
                </label>
                <Input
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) => onInputChange('first_name', e.target.value)}
                  placeholder="Enter your first name"
                  className={desktopInputClass}
                  leftElement={<User className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>

              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Last Name
                </label>
                <Input
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) => onInputChange('last_name', e.target.value)}
                  placeholder="Enter your last name"
                  className={desktopInputClass}
                  leftElement={<User className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[240px_1fr] gap-8 border-b border-[rgba(45,41,35,0.12)] px-8 py-8 dark:border-[#171717]">
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              Contact Information
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => onInputChange('email', e.target.value)}
                  className={`${desktopInputClass} dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-white`}
                  leftElement={<Mail className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                  error={validationErrors.email}
                />
              </div>

              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Country
                </label>
                <Input
                  type="text"
                  value={formData.country || ''}
                  onChange={(e) => onInputChange('country', e.target.value)}
                  placeholder="Enter your country"
                  className={desktopInputClass}
                  leftElement={<MapPin className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[240px_1fr] gap-8 border-b border-[rgba(45,41,35,0.12)] px-8 py-8 dark:border-[#171717]">
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              Professional Information
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Company Name
                </label>
                <Input
                  type="text"
                  value={formData.company_name || ''}
                  onChange={(e) => onInputChange('company_name', e.target.value)}
                  placeholder="Enter your company"
                  className={desktopInputClass}
                  leftElement={<Building className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>

              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Job Title
                </label>
                <Input
                  type="text"
                  value={formData.job_title || ''}
                  onChange={(e) => onInputChange('job_title', e.target.value)}
                  placeholder="Enter your job title"
                  className={desktopInputClass}
                  leftElement={<Briefcase className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[240px_1fr] gap-8 border-b border-[rgba(45,41,35,0.12)] px-8 py-8 dark:border-[#171717]">
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              Investment Preferences
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Minimum Investment Amount
                </label>
                <Input
                  type="number"
                  value={formData.preferred_investment_amount_min || ''}
                  onChange={(e) => onInputChange('preferred_investment_amount_min', parseFloat(e.target.value))}
                  placeholder="10 000"
                  className={`${desktopInputClass} dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-white`}
                  leftElement={<DollarSign className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                />
              </div>

              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  Maximum Investment Amount
                </label>
                <Input
                  type="number"
                  value={formData.preferred_investment_amount_max || ''}
                  onChange={(e) => onInputChange('preferred_investment_amount_max', parseFloat(e.target.value))}
                  placeholder="20 000 000"
                  className={`${desktopInputClass} dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-white`}
                  leftElement={<DollarSign className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                  error={validationErrors.preferred_investment_amount_max}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[240px_1fr] gap-8 px-8 py-8">
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              Social Accounts
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  X Account
                  {profile.x_account_verified && (
                    <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                  )}
                </label>
                <Input
                  type="text"
                  value={formData.x_account || ''}
                  onChange={(e) => onInputChange('x_account', e.target.value)}
                  placeholder="@username"
                  className={desktopInputClass}
                  leftElement={<Twitter className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                  error={validationErrors.x_account}
                />
              </div>

              <div className="space-y-3">
                <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
                  LinkedIn
                  {profile.linkedin_account_verified && (
                    <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                  )}
                </label>
                <Input
                  type="url"
                  value={formData.linkedin || ''}
                  onChange={(e) => onInputChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  className={desktopInputClass}
                  leftElement={<Linkedin className="w-4 h-4 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
                  error={validationErrors.linkedin}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center border-t border-[rgba(45,41,35,0.12)] px-8 py-7 dark:border-[#171717]">
            <Button
              type="submit"
              variant="primary"
              disabled={updating}
              isLoading={updating}
              className="!h-[48px] !rounded-none !bg-[#2d2923] !px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] !text-white hover:!bg-[#2d2923] dark:!bg-[#fdfdfd] dark:!text-[#2d2923] dark:hover:!bg-[#fdfdfd]"
            >
              {updating ? (
                'Updating...'
              ) : (
                <span className="inline-flex items-center gap-3">
                  <span>UPDATE PROFILE</span>
                  <span className="inline-flex items-center border border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.12)] px-[6px] py-[4px] font-plus-jakarta text-[11px] font-normal leading-[12px] text-white dark:border-[rgba(45,41,35,0.16)] dark:bg-[rgba(45,41,35,0.12)] dark:text-[#2d2923]">
                    Press Enter
                  </span>
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="grid grid-cols-[788px_787px] gap-4">
        <div className="grid grid-cols-2 gap-4 w-[788px]">
          <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
            <div className="absolute left-6 top-6 flex h-[76px] flex-col justify-between">
              <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Investment Range
              </p>
              {hasMinAmount || hasMaxAmount ? (
                <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {hasMinAmount ? (
                    <>
                      <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                      <span>{` ${formatCurrencyDesktop(profile.preferred_investment_amount_min).replace('$', '')}`}</span>
                    </>
                  ) : (
                    <span>Not specified</span>
                  )}
                  <span>{` - `}</span>
                  {hasMaxAmount ? (
                    <>
                      <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                      <span>{` ${formatCurrencyDesktop(profile.preferred_investment_amount_max).replace('$', '')}`}</span>
                    </>
                  ) : (
                    <span>Not specified</span>
                  )}
                </p>
              ) : (
                <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Not specified
                </p>
              )}
            </div>
          </div>
          <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
            <div className="absolute left-6 top-6 flex h-[76px] flex-col justify-between">
              <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Company
              </p>
              <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {profile.company_name || 'Not specified'}
              </p>
            </div>
          </div>
          <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
            <div className="absolute left-6 top-6 flex h-[76px] flex-col justify-between">
              <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Location
              </p>
              <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {countryDisplay}
              </p>
            </div>
          </div>
          <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
            <div className="absolute left-6 top-6 flex h-[76px] flex-col justify-between">
              <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Industries
              </p>
              <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {(profile.industries_of_interest || []).length}
              </p>
            </div>
          </div>
        </div>

        <div className="h-[342px] w-[787px] border border-white bg-[rgba(255,255,255,0.32)] px-8 py-8 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
          <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            Investment Preferences
          </h3>

          <div className="mt-8 flex gap-[17px]">
            <div className="w-[353px] border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
              <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                Buy Interest
              </p>
              <div className="mt-[14px] flex max-h-[74px] min-h-[30px] flex-wrap content-start gap-[10px] overflow-y-auto pr-1">
                {(profile.companies_of_interest_buy || []).slice(0, 4).map((item) => (
                  <span
                    key={item}
                    className="inline-flex h-[30px] max-w-full items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap border border-[#1fb845] bg-white px-[12px] py-[2px] font-plus-jakarta text-[12px] font-semibold uppercase leading-[16px] text-[#1fb845] dark:bg-transparent"
                    title={item}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="w-[353px] border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
              <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                Sell Interest
              </p>
              <div className="mt-[14px] flex max-h-[74px] min-h-[30px] flex-wrap content-start gap-[10px] overflow-y-auto pr-1">
                {(profile.companies_of_interest_sell || []).slice(0, 4).map((item) => (
                  <span
                    key={item}
                    className="inline-flex h-[30px] max-w-full items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap border border-[#ff540c] bg-white px-[12px] py-[2px] font-plus-jakarta text-[12px] font-semibold uppercase leading-[16px] text-[#ff540c] dark:bg-transparent"
                    title={item}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 w-[353px] border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              Industries
            </p>
            <div className="mt-[14px] flex max-h-[74px] min-h-[30px] flex-wrap content-start gap-[10px] overflow-y-auto pr-1">
              {(profile.industries_of_interest || []).slice(0, 4).map((item) => (
                <span
                  key={item}
                  className="inline-flex h-[30px] max-w-full items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap border border-[rgba(45,41,35,0.4)] bg-white px-[12px] py-[2px] font-plus-jakarta text-[12px] font-medium uppercase leading-[16px] text-[rgba(45,41,35,0.64)] dark:border-[rgba(253,253,253,0.32)] dark:bg-transparent dark:text-[rgba(253,253,253,0.64)]"
                  title={item}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
